/**
 * fcmUtils.js
 *
 * Firebase Cloud Messaging helpers:
 *  - initFCM()        — request permission + get token + store in Firestore
 *  - getFCMToken()    — returns cached token or fetches fresh one
 *  - storeFCMToken()  — saves token to Firestore under users/{uid}/fcmTokens
 *  - sendServerAlert() — calls /api/send-notification with token + SMS
 *
 * VAPID key: Get from Firebase Console → Project Settings → Cloud Messaging
 *            → Web Push certificates → Generate key pair → copy the public key
 * Then set REACT_APP_FCM_VAPID_KEY in your .env.local and Vercel env vars.
 */

import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { API_BASE } from '../Pages/SquareDashboard/squareDashboardUtils';

// VAPID public key — set this in .env.local as REACT_APP_FCM_VAPID_KEY
// Get it from: Firebase Console → Project Settings → Cloud Messaging → Web Push certificates
const VAPID_KEY = process.env.REACT_APP_FCM_VAPID_KEY || '';

let _messaging = null;
let _cachedToken = null;

function getMessagingInstance() {
  if (!_messaging) {
    try {
      // Dynamically import the Firebase app that's already initialized
      const { getApps } = require('firebase/app');
      if (getApps().length === 0) return null;
      _messaging = getMessaging();
    } catch (e) {
      console.warn('[FCM] Could not get messaging instance:', e.message);
      return null;
    }
  }
  return _messaging;
}

/**
 * Request notification permission and get FCM token.
 * Returns { token, error } — token may be null if permission denied or VAPID not set.
 */
export async function initFCM(uid = null) {
  if (!('Notification' in window)) return { token: null, error: 'not_supported' };
  if (!VAPID_KEY) {
    console.warn('[FCM] REACT_APP_FCM_VAPID_KEY not set. FCM push disabled.');
    return { token: null, error: 'no_vapid_key' };
  }

  try {
    // Request browser permission
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return { token: null, error: 'permission_denied' };

    // Get FCM token
    const messaging = getMessagingInstance();
    if (!messaging) return { token: null, error: 'messaging_unavailable' };

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: await navigator.serviceWorker.ready,
    });

    if (!token) return { token: null, error: 'no_token_returned' };

    _cachedToken = token;

    // Persist token to Firestore so server can push to this device
    if (uid) {
      await storeFCMToken(uid, token);
    }

    return { token, error: null };
  } catch (e) {
    console.error('[FCM] initFCM error:', e);
    return { token: null, error: e.message };
  }
}

/**
 * Store FCM token in Firestore under fcmTokens/{token}
 * so the server can look up all owner devices.
 */
export async function storeFCMToken(uid, token) {
  try {
    const tokenRef = doc(db, 'fcmTokens', token);
    await setDoc(tokenRef, {
      uid,
      token,
      platform: 'web',
      userAgent: navigator.userAgent,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (e) {
    console.warn('[FCM] Failed to store token:', e.message);
  }
}

/**
 * Get cached FCM token (set after initFCM is called).
 */
export function getCachedFCMToken() {
  return _cachedToken;
}

/**
 * Listen for foreground FCM messages (when app is open).
 * Returns unsubscribe function.
 */
export function onForegroundMessage(callback) {
  const messaging = getMessagingInstance();
  if (!messaging) return () => {};
  return onMessage(messaging, payload => {
    console.log('[FCM] Foreground message:', payload);
    callback(payload);
  });
}

/**
 * Call /api/send-notification to dispatch SMS + FCM push from the server.
 * This is what actually reaches your phone even when the browser is closed.
 *
 * @param {Object} opts
 * @param {string} opts.alertKey   - unique dedup key (e.g. 'rush_alert')
 * @param {string} opts.title      - notification title
 * @param {string} opts.body       - notification body
 * @param {string} opts.severity   - 'critical' | 'high' | 'medium'
 * @param {string[]} opts.channels - ['sms', 'fcm'] — which channels to use
 */
export async function sendServerAlert({ alertKey, title, body, severity = 'high', channels = ['sms', 'fcm'] }) {
  try {
    const fcmTokens = _cachedToken ? [_cachedToken] : [];
    const res = await fetch(`${API_BASE}/api/send-notification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ alertKey, title, body, severity, channels, fcmTokens }),
    });
    const data = await res.json();
    if (data.skipped) {
      console.log('[Notify] Skipped:', data.reason);
    } else {
      console.log('[Notify] Sent:', data.results);
    }
    return data;
  } catch (e) {
    console.error('[Notify] sendServerAlert failed:', e.message);
    return { ok: false, error: e.message };
  }
}
